import axios from 'axios';
import {IHash} from "./framework/CommonInterfaces";
import * as _ from 'lodash';
import ItemInfo from "./ItemInfo";
import {SimRunes} from "./calculation/CalculationObjects";

export interface RiotChampStats{
	hp:number;
	hpperlevel:number;
	mp:number;
	mpperlevel:number;
	movespeed:number;
	armor:number;
	armorperlevel:number;
	spellblock:number;
	spellblockperlevel:number;
	attackrange:number;
	hpregen:number;
	hpregenperlevel:number;
	mpregen:number;
	mpregenperlevel:number;
	crit:number;
	critperlevel:number;
	attackdamage:number;
	attackdamageperlevel:number;
	attackspeedoffset:number;
	attackspeedperlevel:number;
}

export interface RiotChampJSON{
	stats:RiotChampStats;
	key:string; //but it's a number like "103"
	name:string;
	id:string;
}

interface SCAPIResponse<T>{
	success:boolean,
	error:string,
	data:T
}

export interface RiotCurrentGameRuneCount{
	count:number;
	runeId:number;
}

export interface RiotRawRealTimeParticipant{
	teamId: 100|200,
	spell1Id: 4,
	spell2Id: 7,
	championId: 81,
	profileIconId: 1426,
	summonerName: string,
	bot: false,
	summonerId: 52600314,
	runes:RiotCurrentGameRuneCount[],

}

export interface RiotRune {
	name: 'Lesser Mark of Attack Damage',
	description: '+0.53 attack damage',
	image: {
		full: 'r_1_1.png',
		sprite: 'rune0.png',
		group: 'rune',
		x: 96,
		y: 0,
		w: 48,
		h: 48
	},
	rune: {
		isrune: true,
		tier: 1,
		type: 'red'
	},
	stats: {
		FlatPhysicalDamageMod: 0.525
	},
	tags:string[],
	colloq: null,
	plaintext: null
}

export interface RiotMastery {
	"id": 6361,
	"name": "Stormraider's Surge",
	"description": [
		"Dealing 30% of a champion's max Health within 2.5 seconds grants you 40% Movement Speed and 75% Slow Resistance for 3 seconds (10 second cooldown)."
		],
	"image": {
		"full": "6361.png",
		"sprite": "mastery0.png",
		"group": "gray_mastery",
		"x": 240,
		"y": 96,
		"w": 48,
		"h": 48
	},
}

export interface RiotSummonerSpell {
	"id": "SummonerBarrier",
	"name": "Barrier",
	"description": "Shields your champion from 115-455 damage (depending on champion level) for 2 seconds.",
	"image": {
		"full": "SummonerBarrier.png",
		"sprite": "spell0.png",
		"group": "spell",
		"x": 0,
		"y": 0,
		"w": 48,
		"h": 48
	},
}

export interface RiotChampionAbilityIconsInfo{
	Q:string,
	W:string,
	E:string,
	R:string,
	P:string
}

export interface RiotChampionAbilitiesInfo{
	Q:RiotChampionAbilityInfo,
	W:RiotChampionAbilityInfo,
	E:RiotChampionAbilityInfo,
	R:RiotChampionAbilityInfo,
	P:RiotChampionAbilityInfo
}

export interface RiotChampionAbilityInfo{
	name:string,
	description:string,
	tooltip?:string,
	image:string,
}

export default class RiotDB{
	static summonersRiftMapId = 11;
	static version:string;
	static isLoaded = false;
	static items:IHash<ItemInfo> = {};
	static itemNames:string[] = [];
	static champions:IHash<RiotChampJSON> = {};
	static championsByKey:IHash<RiotChampJSON> = {};

	static masteries:IHash<RiotMastery> = {};
	static masteriesById:IHash<RiotMastery> = {};

	static runesById:IHash<RiotRune> = {};
	static runesByName:IHash<RiotRune> = {};

	static summonerSpells:IHash<RiotSummonerSpell> = {};
	static summonerSpellsById:IHash<RiotSummonerSpell> = {};

	static championIconsCache:IHash<RiotChampionAbilityIconsInfo> = {};


	static loadP(){

		return (async() => {
			if (RiotDB.isLoaded) return;
			console.time("loadingRiot");
			await RiotDB._updateVersionP();
			await Promise.all([RiotDB._updateItemsP(),RiotDB._updateChampionsP(),RiotDB._updateRunesP(),RiotDB._updateMasteriesP(),RiotDB._updateSummonersP()]);
			console.timeEnd("loadingRiot");

			console.log("done loading riot");
			RiotDB.isLoaded = true;
		})();
	}

	static loadChampionAbilityIconsP(champName:string):Promise<RiotChampionAbilityIconsInfo>{
		return (async() => {

			if(RiotDB.championIconsCache[champName]) return RiotDB.championIconsCache[champName];

			console.log("actually loading icons for " + champName);

			console.log(RiotDB.champions[champName].id);
			let champId = RiotDB.champions[champName].id;

			let url = `https://ddragon.leagueoflegends.com/cdn/${RiotDB.version}/data/en_US/champion/${champId}.json`;

			//TODO Change this because it's so so so bad. Fiddlestick's ID has been changed to Fiddlesticks but to access champion JSON, needs to be /FiddleSticks.json in the URL
			if (champName == "Fiddlesticks"){
				url = `https://ddragon.leagueoflegends.com/cdn/${RiotDB.version}/data/en_US/champion/FiddleSticks.json`;
			}

			let response = await axios.get(url);
			let data = response.data.data[champId];
			let spells = data.spells;

			let ret:RiotChampionAbilityIconsInfo = {
				Q:`http://ddragon.leagueoflegends.com/cdn/${RiotDB.version}/img/spell/${data.spells[0].image.full}`,
				W:`http://ddragon.leagueoflegends.com/cdn/${RiotDB.version}/img/spell/${data.spells[1].image.full}`,
				E:`http://ddragon.leagueoflegends.com/cdn/${RiotDB.version}/img/spell/${data.spells[2].image.full}`,
				R:`http://ddragon.leagueoflegends.com/cdn/${RiotDB.version}/img/spell/${data.spells[3].image.full}`,
				P:`http://ddragon.leagueoflegends.com/cdn/${RiotDB.version}/img/passive/${data.passive.image.full}`
			};

			RiotDB.championIconsCache[champName] = ret;

			return ret;

		})();
	}


	static getChampionIcons(champName:string):RiotChampionAbilityIconsInfo{
		return RiotDB.championIconsCache[champName];
	}

	static getChampionByKey(key:number){
		return RiotDB.championsByKey[""+key];
	}

	static getChampionLoadingImgUrl(championName:string){
		return `https://ddragon.leagueoflegends.com/cdn/img/champion/loading/${RiotDB.champions[championName].id}_0.jpg`
	}

	static getChampionSplashImgUrl(championName:string){
		return `https://ddragon.leagueoflegends.com/cdn/img/champion/splash/${RiotDB.champions[championName].id}_0.jpg`
	}

	static getItemNames(){
		return RiotDB.itemNames;
	}

	static getItemByName(itemName:string){
		return this.items[itemName];
	}

	static getItemImgUrl(itemName:string){
		return `https://ddragon.leagueoflegends.com/cdn/${RiotDB.version}/img/item/${RiotDB.items[itemName].fullImageName}`
	}

	static getChampionNames(){
		return Object.keys(RiotDB.champions);
	}

	static getChampionJSON(riotName:string){
		return this.champions[riotName];
	}

	static getRuneById(runeId:number|string):RiotRune{
		return this.runesById['' + runeId];
	}

	static getMasteryByName(masteryName:string):RiotMastery{
		return this.masteries[masteryName];
	}

	static getMasteryImgUrl(masteryName:string):string{
		return `https://ddragon.leagueoflegends.com/cdn/${RiotDB.version}/img/mastery/${RiotDB.masteries[masteryName].image.full}`
	}

	static getSummonerSpellImgUrl(summonerSpellName:string):string {
		return `https://ddragon.leagueoflegends.com/cdn/${RiotDB.version}/img/spell/${RiotDB.summonerSpells[summonerSpellName].image.full}`
	}

	static getCurrentGameInfoP(name:string, region:string='na1'):Promise<RiotRawRealTimeParticipant[]>{
		return (async() => {
			let url = `https://skillcapped-tools.global.ssl.fastly.net/activegame/${name}/${region}`;
			let axiosResponse = await axios.get(url);
			let response:SCAPIResponse<RiotRawRealTimeParticipant[]> = axiosResponse.data;

			if (response.success == false){
				return [];
			}
			return response.data;

		})();
	}

	static _updateRunesP(){
		return (async() => {
			let url = `https://ddragon.leagueoflegends.com/cdn/${RiotDB.version}/data/en_US/rune.json`;
			let response = await axios.get(url);

			let runesRaw:IHash<RiotRune> = response.data.data;
			for(let key in runesRaw){
				let rune = runesRaw[key];

				RiotDB.runesByName[rune.name] = rune;
				RiotDB.runesById[key] = rune;

			}
		})();
	}

	static _updateItemsP(){
		return (async() => {
			let url = `https://ddragon.leagueoflegends.com/cdn/${RiotDB.version}/data/en_US/item.json`;
			let response = await axios.get(url);

			let itemsRaw = response.data.data;
			for(let key in itemsRaw){
				let item:any = itemsRaw[key];

				//Skip non summoners rift items.
				if(item.maps == undefined || item.maps[RiotDB.summonersRiftMapId] == false){
					continue;
				}

				let itemInfo = new ItemInfo(item);

				RiotDB.items[itemInfo.name] = itemInfo;
				//TODO remove this when we get items working
				RiotDB.itemNames.push(itemInfo.name);

			}
		})();
	}

	static _updateSummonersP(){
		return (async() => {
			let url = `https://ddragon.leagueoflegends.com/cdn/${RiotDB.version}/data/en_US/summoner.json`;
			let response = await axios.get(url);

			let summonersRaw = response.data.data;
			_.forOwn(summonersRaw, (value,key)=>{
				RiotDB.summonerSpells[value.name] = value;
				RiotDB.summonerSpellsById[value.id] = value;
			});
		})();
	}

	static _updateMasteriesP(){
		return (async() => {
			let url = `https://ddragon.leagueoflegends.com/cdn/${RiotDB.version}/data/en_US/mastery.json`;
			let response = await axios.get(url);

			let masteriesRaw = response.data.data;
			_.forOwn(masteriesRaw, (value,key)=>{
				RiotDB.masteries[value.name] = value;
				RiotDB.masteriesById[value.id] = value;
			});
		})();
	}

	static _updateChampionsP(){
		return (async() => {
			let url = `https://ddragon.leagueoflegends.com/cdn/${RiotDB.version}/data/en_US/champion.json`;
			let response = await axios.get(url);
			_.forOwn(response.data.data, (value,key)=>{
				RiotDB.champions[value.name] = value;
				RiotDB.championsByKey[value.key] = value;
			});
		})();
	}



	static _updateVersionP(){
		return (async() => {
			let response = await axios.get("https://ddragon.leagueoflegends.com/realms/na.json");
			RiotDB.version = response.data.v;
			console.log(RiotDB.version);
		})();
	}
}


// WEBPACK FOOTER //
// ./~/tslint-loader!./src/fbobjects/RiotDB.ts